import { type Artist, type Video } from '@prisma/client'
import { type SerializeFrom } from '@remix-run/node'
import { formatDistanceToNowStrict } from 'date-fns'
import { useCallback, useEffect, useRef, useState } from 'react'
import LiteYouTubeEmbed from 'react-lite-youtube-embed'
import 'react-lite-youtube-embed/dist/LiteYouTubeEmbed.css'
import { cn } from '#app/utils/misc.tsx'
import { Button } from './ui/button'
import { Icon } from './ui/icon'

interface VideoComponentProps extends React.ComponentProps<'li'> {
	video: SerializeFrom<Video>
	artist: Artist | null
}

export function VideoComponent({ video, artist }: VideoComponentProps) {
	const [isExpanded, setIsExpanded] = useState(false)
	const handleToggleExpanded = useCallback(() => {
		setIsExpanded(prev => !prev)
	}, [])

	useEffect(() => {
		if (!isExpanded) return
		// We don't do this in the click handler because the state update will change the layout and we want to wait for that to have happened before scrolling to the element
		ref.current?.scrollIntoView({ behavior: 'smooth', block: 'center' })
	}, [isExpanded])

	const ref = useRef<HTMLLIElement>(null)

	const formattedDate = video.publishedAt
		? formatDistanceToNowStrict(video.publishedAt, { addSuffix: true })
		: ''

	const artistNameRegexp = artist?.name && new RegExp(`^${artist.name}`, 'i')
	const hasArtistMatchInTitle =
		artistNameRegexp && video.title.match(artistNameRegexp)
	const titleWithoutArtist = hasArtistMatchInTitle
		? video.title.split(artistNameRegexp)[1]
		: ''

	const [isPlaying, setIsPlaying] = useState(false)

	return (
		<li
			className={cn(
				'flex h-full flex-col justify-between rounded bg-muted pb-1 pt-2',
				{ 'col-span-full': isExpanded },
			)}
			ref={ref}
		>
			{isPlaying
				? <title>{video.title}</title>
				: undefined
			}
			<span className="mb-2 w-full px-2 text-accent-foreground">
				{hasArtistMatchInTitle ? (
					<>
						<strong className="">{artist.name}</strong>
						{titleWithoutArtist}
					</>
				) : (
					video.title
				)}
			</span>
			<div className="flex flex-col">
				<LiteYouTubeEmbed
					id={video.youtubeId}
					title="YouTube video player"
					onIframeAdded={() => setIsPlaying(true)}
				/>
				<div className="mt-1 flex items-center justify-between px-1">
					<Button
						type="button"
						onClick={handleToggleExpanded}
						variant="ghost"
						size="sm"
						className="invisible h-8 sm:visible"
						title={isExpanded ? 'Shrink video player' : 'Expand video player'}
					>
						<Icon
							name={isExpanded ? 'exit-full-screen' : 'enter-full-screen'}
						/>
					</Button>
					<span className="text-muted-foreground">{formattedDate}&nbsp;</span>
				</div>
			</div>
		</li>
	)
}
